import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import { GlassItemType, SizingItemsType } from 'data/types';
import { useState } from 'react';
import { isNotEmpty } from 'utils/helpers';
import {
  getValidSizingAttributeOptions,
  isValidSizingItem
} from 'utils/sizing';

type VerticalGlassProps = {
  glass: GlassItemType;
  updateFn: (glassId: string, updates: Partial<GlassItemType>) => void;
  duplicateFn: (glassId: string) => void;
  deleteFn: (glassId: string) => void;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
  defaultExpandAdvanced?: boolean;
};

export default function VerticalGlassComponent({
  glass,
  updateFn,
  duplicateFn,
  deleteFn,
  updateDefaultSizingFn,
  defaultExpandAdvanced = false
}: VerticalGlassProps) {
  const defaultValues = useAppSelector(
    (state) => state.data.defaultSizingItems.verticalGlass
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(
    defaultExpandAdvanced
  );
  const [animate] = useAutoAnimate({ duration: 100 });
  const [deleteQueued, setDeleteQueued] = useState(false);
  const sizingItemType = 'verticalGlass';
  const isModified = Object.keys(defaultValues)
    .filter((key) => isNotEmpty(defaultValues[key]))
    .some((key) => glass[key] !== defaultValues[key]);

  const handleUpdateWithValidation = (changes: Partial<GlassItemType>) => {
    const newGlass: GlassItemType = {
      ...glass,
      ...changes
    };
    const isValid = isValidSizingItem(sizingItemType, newGlass);

    updateFn(glass.uniqueId, {
      ...newGlass,
      valid: isValid
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const handleDuplicateClick = () => {
    duplicateFn(glass.uniqueId);
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteFn(glass.uniqueId);
    setDeleteQueued(false);
  };

  const setAsDefault = () => {
    updateDefaultSizingFn({
      verticalGlass: {
        ...glass,
        uniqueId: undefined,
        valid: undefined,
        width: undefined,
        height: undefined,
        roofOverhangFeet: undefined,
        distanceTopOfWindowToRoofOverhang: undefined
      }
    });
  };

  const classes = clsx(
    'flex w-full flex-col divide-y divide-gray-300 rounded-xl bg-gray-200 px-5 border-2',
    glass.valid ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div
      key={glass.uniqueId}
      id={`fenestration-${glass.uniqueId}`}
      className={classes}
      ref={animate}
    >
      <div className='flex min-h-[54px] items-center gap-5'>
        <div className='font-semibold'>{glass.fenestrationType}</div>
        <div className='w-[130px]'>
          <FormInput
            value={glass.width}
            placeholder='Width (ft)'
            prefix='Width: '
            type='number'
            suffix=' ft'
            min={1}
            onChange={(value) => handleUpdateWithValidation({ width: value })}
            required
          />
        </div>
        <div className='w-[130px]'>
          <FormInput
            value={glass.height}
            placeholder='Height (ft)'
            prefix='Height: '
            type='number'
            suffix=' ft'
            min={1}
            onChange={(value) => handleUpdateWithValidation({ height: value })}
            required
          />
        </div>
        {!glass.valid && (
          <span className='text-red-500'>Invalid Attributes!</span>
        )}
        <div className='ml-auto flex items-center gap-2'>
          <button
            className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
            type='button'
            onClick={toggleAdvancedOptions}
          >
            <span>Advanced</span>
            {showAdvancedOptions ? (
              <ChevronUpIcon className='h-5 w-5' />
            ) : (
              <ChevronDownIcon className='h-5 w-5' />
            )}
            <Tooltip
              text={
                showAdvancedOptions
                  ? 'Hide Advanced Options'
                  : 'Show Advanced Options'
              }
            />
          </button>
          <button
            className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={handleDuplicateClick}
          >
            <DocumentDuplicateIcon className='h-5 w-5' />
            <Tooltip text='Duplicate Window/Glass' />
          </button>
          <button
            className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
            type='button'
            onClick={handleDeleteClick}
            onMouseLeave={() => setDeleteQueued(false)}
          >
            <TrashIcon className='h-5 w-5' />
            <Tooltip
              text={
                deleteQueued
                  ? 'Click Again to Confirm'
                  : `Delete ${glass.fenestrationType}`
              }
            />
          </button>
        </div>
      </div>
      {showAdvancedOptions && (
        <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
          <FormInput
            value={glass.panes}
            label='Panes'
            type='select'
            options={getValidSizingAttributeOptions(
              sizingItemType,
              glass,
              'panes'
            )}
            onChange={(value) => {
              handleUpdateWithValidation({
                panes: value
              });
            }}
            required
            preventDeselection
          />
          <FormInput
            value={glass.constructionType}
            label='Construction Type'
            type='select'
            options={getValidSizingAttributeOptions(
              sizingItemType,
              glass,
              'constructionType'
            )}
            onChange={(value) => {
              handleUpdateWithValidation({
                constructionType: value
              });
            }}
            required
            preventDeselection
          />
          {glass.fenestrationType !== 'French Door' && (
            <FormInput
              value={glass.sash}
              label='Sash Type'
              type='select'
              options={getValidSizingAttributeOptions(
                sizingItemType,
                glass,
                'sash'
              )}
              onChange={(value) => {
                handleUpdateWithValidation({
                  sash: value
                });
              }}
              required
              preventDeselection
            />
          )}
          <FormInput
            value={glass.bugScreen}
            label='Bug Screen'
            type='select'
            options={['Full Indoor', 'None']}
            onChange={(value) => {
              handleUpdateWithValidation({
                bugScreen: value
              });
            }}
            required
            preventDeselection
          />
          <FormInput
            value={glass.roofOverhangFeet}
            label='Roof Overhang'
            subLabel='(ft)'
            type='number'
            suffix=' ft'
            min={1}
            onChange={(value) => {
              handleUpdateWithValidation({
                roofOverhangFeet: value
              });
            }}
            required
          />
          <FormInput
            value={glass.distanceTopOfWindowToRoofOverhang}
            label='Top of Window to Roof'
            subLabel='(ft)'
            type='number'
            suffix=' ft'
            min={1}
            onChange={(value) => {
              handleUpdateWithValidation({
                distanceTopOfWindowToRoofOverhang: value
              });
            }}
            required
          />
          {isModified && (
            <div className='flex w-full justify-end'>
              <button
                className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
                type='button'
                onClick={setAsDefault}
              >
                <span>Set as Default</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
